(function () {
  const GIFT_VARIANT_ID = window.GWP_CONFIG?.giftVariantId || 0;
  const MINIMUM_AMOUNT = window.GWP_CONFIG?.minimumAmount || 0;
  const THRESHOLD_CENTS = Math.round(MINIMUM_AMOUNT * 100);
  const EXCLUDED_PRODUCT_IDS = new Set(
    (window.GWP_CONFIG?.excludedProductIds || []).map(Number)
  );

  // Safe aliases for Dawn's pubsub globals (pubsub.js / constants.js).
  // Fall back to no-ops so this script works even if loaded standalone.
  const _publish = typeof publish === 'function' ? publish : () => {};
  const _EVENTS  = typeof PUB_SUB_EVENTS !== 'undefined'
    ? PUB_SUB_EVENTS
    : { cartUpdate: 'cart-update' };

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const CART_ENDPOINT_RE = /\/cart\/(add|change|update|clear)\.js(\?|$)/;
 
  let syncInFlight = false;
  let syncQueued = false;
  let lastKnownSignature = '';
  let lastKnownHasGift = false;
  let customerRemovedGift = false;
 
  async function getCart() {
    const res = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!res.ok) throw new Error('Failed to load cart');
    return res.json();
  }
 
  function getGiftLine(cart) {
    return cart.items.find(item => item.variant_id === GIFT_VARIANT_ID) || null;
  }
 
  function getQualifyingSubtotal(cart) {
    return cart.items.reduce((sum, item) => {
      if (item.variant_id === GIFT_VARIANT_ID) return sum;
      if (EXCLUDED_PRODUCT_IDS.has(item.product_id)) return sum;
      return sum + item.final_line_price;
    }, 0);
  }
 
  function getCartSignature(cart) {
    return JSON.stringify(
      cart.items.map(item => ({
        key: item.key,
        q: item.quantity,
        fp: item.final_line_price
      }))
    );
  }
 
  function getCartPageSectionIds() {
    const itemsId = document.getElementById('main-cart-items')?.dataset.id;
    const footerId = document.getElementById('main-cart-footer')?.dataset.id;
    return { itemsId, footerId };
  }

  async function mutateCart(endpoint, body) {
    const sectionsToRequest = [...SECTIONS];

    if (window.location.pathname === '/cart') {
      const { itemsId, footerId } = getCartPageSectionIds();
      if (itemsId) sectionsToRequest.push(itemsId);
      if (footerId) sectionsToRequest.push(footerId);
    }

    const payload = {
      ...body,
      sections: sectionsToRequest,
      sections_url: window.location.pathname
    };
 
    const res = await fetch(`${window.Shopify.routes.root}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    });
 
    if (!res.ok) {
      let error;
      try {
        error = await res.json();
      } catch {
        error = { message: 'Cart mutation failed' };
      }
      throw error;
    }
 
    return res.json();
  }
 
  function renderSectionHtml(html, selector, target) {
    if (!html || !target) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const replacement = doc.querySelector(selector);
    if (replacement) target.innerHTML = replacement.innerHTML;
  }
 
  function refreshSectionsFromResponse(response) {
    if (!response || !response.sections) return;

    if (response.sections['cart-drawer']) {
      const doc = new DOMParser().parseFromString(response.sections['cart-drawer'], 'text/html');

      // Replace drawer inner content
      const replacement = doc.querySelector('.drawer__inner');
      const target = document.querySelector('cart-drawer .drawer__inner');
      if (replacement && target) target.innerHTML = replacement.innerHTML;

      // Sync is-empty class on the <cart-drawer> element so the empty state
      // CSS and focus-trap logic work correctly when the cart becomes empty.
      const newCartDrawer = doc.querySelector('cart-drawer');
      const currentCartDrawer = document.querySelector('cart-drawer');
      if (newCartDrawer && currentCartDrawer) {
        currentCartDrawer.classList.toggle('is-empty', newCartDrawer.classList.contains('is-empty'));
      }
    }
 
    const bubbleContainer = document.getElementById('shopify-section-cart-icon-bubble');
    if (response.sections['cart-icon-bubble']) {
      const doc = new DOMParser().parseFromString(response.sections['cart-icon-bubble'], 'text/html');

      // Update cart count bubble directly, or insert it if the cart was previously empty
      const newBubble = doc.querySelector('.cart-count-bubble');
      const currentBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      const currentIcon = document.getElementById('cart-icon-bubble');
      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      } else if (newBubble && currentIcon) {
        currentIcon.insertAdjacentHTML('beforeend', newBubble.outerHTML);
      } else if (!newBubble && currentBubble) {
        currentBubble.remove();
      }

      // Update the section wrapper for any other content it may contain
      if (bubbleContainer) {
        const replacement =
          doc.getElementById('shopify-section-cart-icon-bubble') ||
          doc.querySelector('.shopify-section');
        if (replacement) bubbleContainer.innerHTML = replacement.innerHTML;
      }
    }

    // Re-render cart page sections when on /cart
    if (window.location.pathname === '/cart') {
      const { itemsId, footerId } = getCartPageSectionIds();

      if (itemsId && response.sections[itemsId]) {
        const doc = new DOMParser().parseFromString(response.sections[itemsId], 'text/html');
        const newContents = doc.querySelector('#main-cart-items .js-contents');
        const currentContents = document.querySelector('#main-cart-items .js-contents');
        if (newContents && currentContents) currentContents.innerHTML = newContents.innerHTML;

        // Sync is-empty on <cart-items> so the cart__warnings empty state
        // shows/hides correctly via CSS when the cart becomes empty.
        const newCartItems = doc.querySelector('cart-items');
        const currentCartItems = document.querySelector('cart-items');
        if (newCartItems && currentCartItems) {
          currentCartItems.classList.toggle('is-empty', newCartItems.classList.contains('is-empty'));
        }
      }

      if (footerId && response.sections[footerId]) {
        const doc = new DOMParser().parseFromString(response.sections[footerId], 'text/html');
        const newContents = doc.querySelector('#main-cart-footer .js-contents');
        const currentContents = document.querySelector('#main-cart-footer .js-contents');
        if (newContents && currentContents) currentContents.innerHTML = newContents.innerHTML;

        // Sync is-empty on #main-cart-footer so Dawn's CSS hides/shows .cart__footer
        // when the cart becomes empty (mirrors what cart.js CartItems does natively).
        const newFooter = doc.getElementById('main-cart-footer');
        const currentFooter = document.getElementById('main-cart-footer');
        if (newFooter && currentFooter) {
          currentFooter.classList.toggle('is-empty', newFooter.classList.contains('is-empty'));
        }
      }
    }
  }
 
  let toastTimer;
  function showToast(message) {
    let toast = document.getElementById('gwp-toast');
 
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'gwp-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      Object.assign(toast.style, {
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%) translateY(20px)',
        background: '#1a1a1a',
        color: '#fff',
        padding: '12px 20px',
        borderRadius: '8px',
        fontSize: '14px',
        fontFamily: 'inherit',
        zIndex: '99999',
        opacity: '0',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        pointerEvents: 'none',
        maxWidth: '90vw',
        textAlign: 'center',
        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      });
      document.body.appendChild(toast);
    }
 
    clearTimeout(toastTimer);
    toast.textContent = message;
 
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateX(-50%) translateY(0)';
    });
 
    toastTimer = setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(-50%) translateY(20px)';
    }, 3500);
  }
 
  async function addGift() {
    console.log('Adding gift to cart');
    const response = await mutateCart('cart/add.js', {
      items: [{ id: GIFT_VARIANT_ID, quantity: 1 }]
    });
    refreshSectionsFromResponse(response);
    showToast('Your free gift was added to the cart.');
    _publish(_EVENTS.cartUpdate, { source: 'gift-with-purchase', cartData: response });
  }

  async function removeGift(giftLine) {
    const response = await mutateCart('cart/change.js', {
      id: giftLine.key,
      quantity: 0
    });
    refreshSectionsFromResponse(response);
    showToast('Your free gift was removed because the cart no longer qualifies.');
    _publish(_EVENTS.cartUpdate, { source: 'gift-with-purchase', cartData: response });
  }

  async function syncGift() {
    console.log('Syncing gift with cart');
    if (syncInFlight) {
      syncQueued = true;
      return;
    }

    syncInFlight = true;
 
    try {
      const cart = await getCart();
      const signature = getCartSignature(cart);
 
      if (signature === lastKnownSignature) return;
      lastKnownSignature = signature;
 
      const giftLine = getGiftLine(cart);
      const hasGift = Boolean(giftLine);
      const qualifyingSubtotal = getQualifyingSubtotal(cart);
      const qualifies = qualifyingSubtotal >= THRESHOLD_CENTS;

      // If the cart no longer qualifies, clear the customer-removal flag
      // so the gift can be re-offered if they qualify again later.
      if (!qualifies) customerRemovedGift = false;

      // Cart still qualifies but gift vanished — customer removed it manually.
      if (qualifies && !hasGift && lastKnownHasGift) customerRemovedGift = true;

      lastKnownHasGift = hasGift;

      if (qualifies && !hasGift && !customerRemovedGift) {
        await addGift();
        lastKnownHasGift = true;
        lastKnownSignature = '';
      } else if (!qualifies && hasGift) {
        await removeGift(giftLine);
        lastKnownHasGift = false;
        lastKnownSignature = '';
      }
    } catch (error) {
      console.error('GWP sync failed', error);
    } finally {
      syncInFlight = false;
 
      if (syncQueued) {
        syncQueued = false;
        queueSync();
      }
    }
  }
 
  let syncTimer;
  function queueSync(delay = 250) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncGift, delay);
  }
 
  const nativeFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await nativeFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
 
    if (CART_ENDPOINT_RE.test(url)) {
      queueSync();
    }
 
    return response;
  };
 
  // Subscribe to Dawn's pubsub so any cart change (theme or apps) triggers a sync.
  // Done inside DOMContentLoaded to guarantee pubsub.js has already run.
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        console.log('GWP received cart update event', event);
        // Avoid re-triggering on our own mutations
        if (event?.source === 'gift-with-purchase') return;
        console.log('GWP queuing sync from cart update event');
        queueSync();
      });
    }
    queueSync(0);
  });

  document.addEventListener('cart:updated', () => queueSync());
  window.addEventListener('pageshow', () => queueSync(0));

  // Expose for use by cart.js / other theme scripts
  window.GWP = { queueSync };
})();